import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
  makeStyles,
} from "@material-ui/core";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import type { ReactElement } from "react";

import { useAppProxySessionGuard } from "./sessionGuard";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    padding: theme.spacing(2),
    maxWidth: 460,
    width: "100%",
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  iconWrapper: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    borderRadius: "50%",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.04)",
    color: theme.palette.primary.main,
  },
  titleText: {
    fontWeight: 600,
  },
  content: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  description: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  actions: {
    padding: theme.spacing(1, 2),
    justifyContent: "flex-end",
  },
  button: {
    minWidth: 120,
    fontWeight: 600,
    textTransform: "none",
  },
}));

export interface SessionExpiredDialogProps {
  open?: boolean;
  onContinue?: () => void;
  /**
   * Heartbeat opt-in (uso interno/test). Sin prop NO se crea interval:
   * la comprobación ocurre al recuperar visibilidad o vía eventos globales.
   */
  pollingIntervalMs?: number;
}

/**
 * Diálogo accesible de sesión caducada para Azure AD / Entra Application Proxy.
 * Bloquea la interacción en segundo plano y ofrece reautenticación top-level vía "Continuar".
 */
export const SessionExpiredDialog = ({
  open: controlledOpen,
  onContinue,
  pollingIntervalMs,
}: SessionExpiredDialogProps): ReactElement => {
  const classes = useStyles();
  const { isExpired } = useAppProxySessionGuard({ pollingIntervalMs });

  const isOpen = controlledOpen ?? isExpired;

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      return;
    }

    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  return (
    <Dialog
      open={isOpen}
      disableEscapeKeyDown
      aria-labelledby="session-expired-dialog-title"
      aria-describedby="session-expired-dialog-description"
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle
        id="session-expired-dialog-title"
        disableTypography
        className={classes.titleContainer}
      >
        <div className={classes.iconWrapper} aria-hidden="true">
          <LockOutlinedIcon />
        </div>
        <Typography variant="h6" component="h2" className={classes.titleText}>
          Sesión caducada
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <DialogContentText
          id="session-expired-dialog-description"
          className={classes.description}
        >
          Tu sesión ha caducado. Para continuar utilizando la plataforma, vuelve
          a autenticarte con tu cuenta corporativa.
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          onClick={handleContinue}
          className={classes.button}
        >
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
};
